import {i18n} from '@online-offer/i18n';
import {Alert} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {type DisplayedFinancialProductQuote} from 'features/financial-products';

import {
  FinancialProductOffer,
  FinancialProductPreferenceRefusal,
} from '../../api/types/onlineOfferTypes';
import {type FinancialProductPreferenceContextValue} from './FinancialProductPreferenceContext';
import {getFinancialProductUnavailableReasonLabelKey} from './getFinancialProductUnavailableReasonLabelKey';

/**
 * Only refusals; a failed save that waits for a retry gets its own notice. A signed snapshot
 * supersedes the contract-closed refusal.
 */
const toRefusalNotice = (refusal: FinancialProductPreferenceRefusal | null) =>
  match(refusal)
    .with({kind: 'REFUSED', reason: 'PREFERENCE_REVISION_CONFLICT'}, () => ({
      variant: 'info' as const,
      message: i18n.t('financialProducts.preference.sellerChanged'),
    }))
    .with({kind: 'REFUSED', reason: 'NOT_IN_OFFER_STATE'}, () => ({
      variant: 'warning' as const,
      message: i18n.t('financialProducts.preference.noLongerChangeable'),
    }))
    .with({kind: 'REFUSED', reason: 'PRODUCT_UNAVAILABLE'}, () => ({
      variant: 'warning' as const,
      message: i18n.t('financialProducts.preference.choiceUnavailable'),
    }))
    .otherwise(() => null);

/**
 * A shown quote decides availability as the control moves: a local refusal names its reason and
 * any priced result hides the notice. Without a quote the stored offer decides, as before.
 */
const toUnavailableNotice = (
  offer: FinancialProductOffer,
  displayed: DisplayedFinancialProductQuote,
  rebased: boolean
) =>
  match(displayed)
    .with({source: 'STORED'}, () => null)
    .with({source: 'LOCAL', quote: {availability: 'AVAILABLE'}}, () => null)
    .with({source: 'LOCAL', quote: {availability: 'UNAVAILABLE'}}, (local) => ({
      reason: local.quote.unavailableReason,
    }))
    .with({source: 'NONE'}, () =>
      offer.availability === 'UNAVAILABLE' && !rebased ? {reason: offer.unavailableReason} : null
    )
    .exhaustive();

interface FinancialProductNoticesProps extends TestIdProps {
  offer: FinancialProductOffer;
  displayed: DisplayedFinancialProductQuote;
  refusal: FinancialProductPreferenceRefusal | null;
  rebased: boolean;
  quoteLifecycle: FinancialProductPreferenceContextValue['quoteLifecycle'];
}

export function FinancialProductNotices(props: FinancialProductNoticesProps) {
  const notice = toRefusalNotice(props.refusal);
  const unavailableNotice = toUnavailableNotice(props.offer, props.displayed, props.rebased);
  const showsRefusal = isNotNil(notice);
  const showsRebased = props.offer.availability === 'UNAVAILABLE' && props.rebased;
  const showsUnavailable = isNotNil(unavailableNotice);
  // Without a shown quote the result panel already says the save failed.
  const showsNotSavedYet = props.quoteLifecycle === 'failed' && props.displayed.source !== 'NONE';

  if (!showsRefusal && !showsNotSavedYet && !showsRebased && !showsUnavailable) {
    return null;
  }

  const unavailableLabelKey = getFinancialProductUnavailableReasonLabelKey(
    unavailableNotice?.reason ?? null
  );

  return (
    <VStack spacing={2}>
      <Show when={showsRefusal}>
        <Alert
          type="inline"
          variant={notice?.variant}
          message={notice?.message}
          data-testid={suffixTestId('preferenceNotice', props)}
        />
      </Show>
      <Show when={showsNotSavedYet}>
        <Alert
          type="inline"
          variant="warning"
          message={i18n.t('financialProducts.preference.notSavedYet')}
          data-testid={suffixTestId('notSavedYet', props)}
        />
      </Show>
      <Show when={showsRebased}>
        <Alert
          type="inline"
          variant="info"
          message={i18n.t('financialProducts.notice.rebased')}
          data-testid={suffixTestId('rebased', props)}
        />
      </Show>
      <Show when={showsUnavailable}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('financialProducts.unavailable.title')}
          message={i18n.t(unavailableLabelKey)}
          data-testid={suffixTestId('unavailable', props)}
        />
      </Show>
    </VStack>
  );
}
